// ---------------------------------------------------------------------------
// Email Agent Analytics — Constants
// ---------------------------------------------------------------------------

import { SENTIMENT_THRESHOLDS } from "@/lib/analytics/sentiment";
import type {
  AnalyticsConfig,
  PipelineStage,
  ReplyOutcome,
  ResolutionCategory,
} from "./types";

// ---- Default analytics config ----------------------------------------------

export const DEFAULT_ANALYTICS_CONFIG: AnalyticsConfig = {
  default_period: "this_month",
  handover_definition: "escalated_and_forwarded",
  silent_close_days: 7,
  sentiment_thresholds: {
    negative_max: SENTIMENT_THRESHOLDS.negativeMax,
    neutral_max: SENTIMENT_THRESHOLDS.neutralMax,
  },
  time_constants: {
    full_handling_min: 10,
    draft_review_min: 2,
    triage_min: 3,
    context_saving_min: 2,
    error_overhead_min: 2,
    working_hours_per_month: 160,
  },
  response_time: {
    include_agent_replies: true,
    include_forwarded: true,
    sla_threshold_hours: 24,
    weekend_days: [],
  },
};

// ---- Period options --------------------------------------------------------

export { PERIOD_OPTIONS } from "@/lib/analytics/period";

// ---- Pipeline stage labels -------------------------------------------------

export const PIPELINE_STAGE_LABELS: Record<PipelineStage, string> = {
  synced: "Synced",
  classified: "Classified",
  crm_processed: "CRM Processed",
  replied: "Replied",
};

// ---- Reply outcome config --------------------------------------------------

export const REPLY_OUTCOME_CONFIG: Record<
  ReplyOutcome,
  { label: string; color: string }
> = {
  error: { label: "Error", color: "#ef4444" },
  escalated: { label: "Escalated", color: "#f97316" },
  forwarded: { label: "Forwarded", color: "#a855f7" },
  sent: { label: "Sent", color: "#22c55e" },
  draft: { label: "Draft", color: "#eab308" },
  skipped: { label: "Skipped", color: "#6b7280" },
  dry_run: { label: "Dry Run", color: "#3b82f6" },
};

/**
 * Outcome priority (index 0 = highest).
 * When a single auto_reply has multiple flags, first match wins.
 */
export const OUTCOME_PRIORITY: ReplyOutcome[] = [
  "error",
  "escalated",
  "forwarded",
  "sent",
  "draft",
  "skipped",
  "dry_run",
];

// ---- Resolution quality colors ---------------------------------------------

export const RESOLUTION_CONFIG: Record<
  ResolutionCategory,
  { label: string; title: string; description: string; color: string; bg: string; text: string }
> = {
  positive: {
    label: "Confirmed",
    title: "Positive",
    description: "Last interaction had positive sentiment",
    color: "#22c55e",
    bg: "bg-green-50",
    text: "text-green-700",
  },
  conditional: {
    label: "No Complaints",
    title: "Conditionally Positive",
    description: "Last interaction had neutral sentiment (no complaints)",
    color: "#3b82f6",
    bg: "bg-blue-50",
    text: "text-blue-700",
  },
  negative: {
    label: "Dissatisfied",
    title: "Negative",
    description: "Last interaction had negative sentiment",
    color: "#ef4444",
    bg: "bg-red-50",
    text: "text-red-700",
  },
  pending: {
    label: "Pending",
    title: "Pending",
    description: "Last interaction is still awaiting response",
    color: "#6b7280",
    bg: "bg-gray-50",
    text: "text-gray-700",
  },
};

// ---- Pipeline stage colors -------------------------------------------------

export const PIPELINE_STAGE_COLORS: Record<PipelineStage, string> = {
  synced: "#3b82f6",
  classified: "#8b5cf6",
  crm_processed: "#f59e0b",
  replied: "#22c55e",
};

// ---- Activity timeline colors ----------------------------------------------

export const TIMELINE_COLORS = {
  inbound: "#3b82f6",
  sent: "#22c55e",
  drafts: "#eab308",
  handovers: "#f97316",
  errors: "#ef4444",
} as const;

// ---- Tool Usage whitelist ---------------------------------------------------

/**
 * Only external business tools shown in the Tool Usage chart.
 * Internal agent tools (assess_sentiment, human_handover, skip_reply,
 * email_forward, capture_customer_info) are excluded — they're already
 * reflected in Reply Outcomes / KPI cards.
 */
export const TOOL_USAGE_WHITELIST = new Set([
  "get_shopify_order",
  "check_calendar_availability",
  "book_calendar_meeting",
]);

// ---- Classification chart palette ------------------------------------------

export const CLASSIFICATION_COLORS = [
  "#3b82f6",
  "#22c55e",
  "#f59e0b",
  "#a855f7",
  "#ef4444",
  "#06b6d4",
  "#ec4899",
  "#6b7280",
] as const;
